import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon        from "@mui/icons-material/Close";
import ChatBubbleIcon   from "@mui/icons-material/ChatBubble";
import ThumbUpAltIcon   from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { POPPINS, HEADER_COLOR } from "./adminConstants";
import DatasourceBreakdown from "./DatasourceBreakdown";

const CHAT_DRAWER_RPP = 25;

const normalizeFeedbackType = (feedbackType) => {
  const value = String(feedbackType || "").trim().toLowerCase();
  if (["down", "thumb_down", "thumbs_down", "dislike", "negative"].includes(value)) return "down";
  if (["up", "thumb_up", "thumbs_up", "like", "positive"].includes(value)) return "up";
  return "";
};

const ChatHistoryDrawer = ({
  chatDrawerUser,
  chatDrawerRows,
  chatDrawerTotal,
  chatDrawerPage,
  chatDrawerLoading,
  chatDrawerError,
  datasourceFilter,
  onDatasourceChange,
  onClose,
  onPageChange,
}) => (
  <Drawer
    anchor="right"
    open={!!chatDrawerUser}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: "100vw", sm: 1800 }, display: "flex", flexDirection: "column" } }}
  >
    {/* Header */}
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      px: 2.5, py: 2, borderBottom: "1px solid #e0e0e0",
      backgroundColor: "#f5f7fb",
    }}>
      <ChatBubbleIcon sx={{ color: "#1976d2", fontSize: 20 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, lineHeight: 1.2 }}>
          Chat History
          {chatDrawerUser && (
            <Chip
              label={`${chatDrawerUser.chatCount} messages`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ...POPPINS, fontSize: "0.65rem", ml: 1, height: 20 }}
            />
          )}
          {datasourceFilter && (
            <Chip
              label={datasourceFilter}
              size="small"
              variant="filled"
              sx={{ ...POPPINS, fontSize: "0.65rem", ml: 0.5, height: 20, backgroundColor: "#e3f2fd", color: "#1565c0" }}
            />
          )}
        </Typography>
        <Typography variant="caption" sx={{ ...POPPINS, color: "#777" }}>
          {chatDrawerUser?.name} &bull; {chatDrawerUser?.email_id}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>

    {/* Datasource filter */}
    <DatasourceBreakdown selectedDatasource={datasourceFilter} onSelect={onDatasourceChange} userId={chatDrawerUser?.user_id} />

    {/* Body */}
    <Box sx={{ flex: 1, overflow: "auto" }}>
      {chatDrawerLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {chatDrawerError && (
        <Alert severity="error" sx={{ m: 2, ...POPPINS }}>{chatDrawerError}</Alert>
      )}

      {!chatDrawerLoading && !chatDrawerError && chatDrawerRows.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={POPPINS}>No chat records found.</Typography>
        </Box>
      )}

      {!chatDrawerLoading && chatDrawerRows.length > 0 && (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Group Name", "Question", "Answer", "Datasource", "Feedback", "Date"].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    ...POPPINS, fontWeight: 700, fontSize: "0.72rem",
                    color: HEADER_COLOR, backgroundColor: "#f5f7fb", whiteSpace: "nowrap",textAlign: "center"
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {chatDrawerRows.map((row, i) => (
              <TableRow key={row.question_id || i} hover sx={{ "&:last-child td": { border: 0 } }}>
                {/* Group Name */}
                <TableCell sx={{ maxWidth: 140 }}>
                  <Tooltip title={row.group_name || "—"}>
                    <Typography variant="body2" sx={{
                      ...POPPINS, fontSize: "0.75rem", fontWeight: 600, color: "#444",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130,
                    }}>
                      {row.group_name || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Question */}
                <TableCell sx={{ maxWidth: 360 }}>
                  <Tooltip title={row.question || "—"}>
                    <Typography variant="body2" sx={{
                      ...POPPINS, fontSize: "0.75rem", color: "#333",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 340,
                    }}>
                      {row.question || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Answer */}
                <TableCell sx={{ maxWidth: 360 }}>
                  <Tooltip title={row.answer || "—"}>
                    <Typography variant="body2" sx={{
                      ...POPPINS, fontSize: "0.75rem", color: "#333",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 340,
                    }}>
                      {row.answer || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Datasource */}
                <TableCell sx={{ textAlign: "center" }}>
                  {row.datasource ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {row.datasource.split(",").map((ds) => ds.trim()).filter(Boolean).map((ds) => (
                        <Chip
                          key={ds}
                          label={ds.charAt(0).toUpperCase() + ds.slice(1)}
                          size="small"
                          variant="outlined"
                          sx={{ ...POPPINS, fontSize: "0.65rem", height: 20 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ ...POPPINS, color: "#bbb" }}>—</Typography>
                  )}
                </TableCell>

                {/* Feedback */}
                <TableCell sx={{ minWidth: 140, textAlign: "center" }}>
                  {(() => {
                    const feedbackType = normalizeFeedbackType(row.feedback_type);
                    if (!feedbackType) {
                      return <Typography variant="caption" sx={{ ...POPPINS, color: "#bbb" }}>-</Typography>;
                    }

                    if (feedbackType === "up") {
                      return (
                        <Chip
                          icon={<ThumbUpAltIcon sx={{ color: "#2e7d32 !important", fontSize: 14 }} />}
                          // label="Thumbs Up"
                          size="small"
                          variant="outlined"
                          sx={{
                            ...POPPINS,
                            fontSize: "0.68rem",
                            height: 22,
                            color: "#2e7d32",
                            border: "none",
                            // borderColor: "#2e7d32",
                            // backgroundColor: "#f1f8e9"
                          }}
                        />
                      );
                    }

                    const reason = row.feedback_reason || "No reason provided";
                    const comment = row.feedback_comment || "No comment provided";
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ ...POPPINS, p: 0.25 }}>
                            <Typography variant="caption" sx={{ ...POPPINS, display: "block", fontWeight: 700 }}>
                              Feedback details
                            </Typography>
                            <Typography variant="caption" sx={{ ...POPPINS, display: "block" }}>
                              Reason: {reason}
                            </Typography>
                            <Typography variant="caption" sx={{ ...POPPINS, display: "block" }}>
                              Comment: {comment}
                            </Typography>
                          </Box>
                        }
                        arrow
                      >
                        <Chip
                          icon={<ThumbDownAltIcon sx={{ color: "#c62828 !important", fontSize: 14 }} />}
                          // label="Thumbs Down"
                          size="small"
                          variant="outlined"
                          sx={{
                            ...POPPINS,
                            fontSize: "0.68rem",
                            height: 22,
                            color: "#c62828",
                            border: "none",
                            // borderColor: "#c62828",
                            // backgroundColor: "#ffebee",
                            cursor: "help"
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                </TableCell>

                {/* Date */}
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  <Typography variant="body2" sx={{ ...POPPINS, fontSize: "0.73rem", color: "#666" }}>
                    {row.timestamp ? new Date(row.timestamp).toLocaleString() : "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>

    {/* Pagination */}
    {chatDrawerTotal > CHAT_DRAWER_RPP && (
      <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
        <TablePagination
          component="div"
          count={chatDrawerTotal}
          page={chatDrawerPage}
          onPageChange={onPageChange}
          rowsPerPage={CHAT_DRAWER_RPP}
          rowsPerPageOptions={[CHAT_DRAWER_RPP]}
          sx={{ ...POPPINS, fontSize: "0.75rem" }}
        />
      </Box>
    )}
  </Drawer>
);

export default ChatHistoryDrawer;
